// Loan comparison math — reducing-balance EMI, optional extra monthly payment
// (payout), and a yearly amortization schedule. Pure frontend, no API.
import { calcEmi } from "@/lib/format";

export const MAX_MONTHS = 600; // 50 years hard cap

export function toMonths(years, months) {
  return Math.max(0, Math.round(Number(years) || 0) * 12 + Math.round(Number(months) || 0));
}

// Validates one loan's inputs. Returns an error string or "".
export function validateLoan({ principal, rate, months, extraMonthly }) {
  if (!(Number(principal) > 0)) return "Enter a principal amount above ₹0";
  if (!(Number(rate) >= 0) || Number(rate) > 50) return "Rate must be between 0% and 50% p.a.";
  if (!(months > 0)) return "Enter a tenure (years and/or months)";
  if (!(Number(extraMonthly) >= 0)) return "Extra payment cannot be negative";
  return "";
}

// Full computation for one loan. Assumes validateLoan passed.
export function computeLoan({ principal, rate, months, extraMonthly = 0 }) {
  const P = Number(principal);
  const r = (Number(rate) || 0) / 12 / 100;
  const n = toMonths(0, months);
  const extra = Number(extraMonthly) || 0;
  const { emi } = calcEmi(P, rate, n);

  // Guard: payment must cover the first month's interest, else balance never falls.
  const firstInterest = P * r;
  const paymentTooLow = r > 0 && emi + extra <= firstInterest;

  let balance = P;
  let totalInterest = 0;
  let m = 0;
  const yearly = [];
  let yearPrincipal = 0;
  let yearInterest = 0;

  if (!paymentTooLow) {
    while (balance > 0.005 && m < MAX_MONTHS) {
      m += 1;
      const interest = balance * r;
      let principalPaid = emi + extra - interest;
      if (principalPaid > balance) principalPaid = balance;
      balance -= principalPaid;
      totalInterest += interest;
      yearPrincipal += principalPaid;
      yearInterest += interest;
      if (m % 12 === 0 || balance <= 0.005) {
        yearly.push({
          period: `Year ${Math.ceil(m / 12)}`,
          months: m,
          principalPaid: yearPrincipal,
          interestPaid: yearInterest,
          balance: Math.max(0, balance),
        });
        yearPrincipal = 0;
        yearInterest = 0;
      }
    }
  }

  const totalRepayment = P + totalInterest;
  return {
    emi,
    scheduledMonths: n,
    payoffMonths: paymentTooLow ? null : m,
    paymentTooLow,
    totalInterest,
    totalRepayment,
    interestPct: P > 0 ? (totalInterest / P) * 100 : 0,
    extraMonthly: extra,
    schedule: yearly,
  };
}

// Always name the lowest-total-repayment offer. Rupee savings lines only make
// sense when principal AND tenure match — a lower EMI alone never makes a loan cheaper.
export function compareLoans(computed, loans) {
  const principals = loans.map((l) => Number(l.principal));
  const tenures = loans.map((l) => l._months);
  const comparable =
    new Set(principals).size === 1 && new Set(tenures).size === 1;
  let bestIdx = 0;
  computed.forEach((c, i) => {
    if (c.totalRepayment < computed[bestIdx].totalRepayment) bestIdx = i;
  });
  const savings = computed.map((c) => c.totalRepayment - computed[bestIdx].totalRepayment);
  return { comparable, bestIdx, savings };
}

// Smart tenure suggestion: the SHORTEST whole-year-shorter tenure whose EMI rises
// by at most 15% (max interest saved within a tolerable EMI bump). Falls back to
// one year shorter when nothing fits the 15% budget. Returns null when N/A.
export function tenureSuggestion({ principal, rate, months, extraMonthly = 0 }) {
  if (months <= 12) return null;
  const base = computeLoan({ principal, rate, months, extraMonthly });
  if (base.paymentTooLow || base.emi <= 0) return null;
  let best = null;
  for (let cand = months - 12; cand >= 12; cand -= 12) {
    const c = computeLoan({ principal, rate, months: cand, extraMonthly });
    if (c.paymentTooLow) continue;
    const saves = base.totalInterest - c.totalInterest;
    if (saves <= 0) continue;
    const emiDelta = c.emi - base.emi;
    const emiDeltaPct = (emiDelta / base.emi) * 100;
    const entry = { months: cand, emi: c.emi, emiDelta, emiDeltaPct, saves };
    if (emiDeltaPct <= 15) {
      best = entry; // iterate from closest-shorter downward; last within budget = shortest = max savings
    } else if (!best) {
      best = entry; // keep the first (least aggressive) over-budget option as fallback
    }
  }
  return best;
}
